import { useEffect, useState } from "react";

import { sub } from "date-fns";
import { generatePath } from "react-router-dom";

import { getTaskStatus, newTask } from "@/api/endpoints/task";
import { postTrackingData } from "@/api/endpoints/track";
import ArrowTopRightOnSquareIcon from "@/assets/icons/arrow-top-right-on-square.svg";
import { TASK_STATUS, TaskStatus } from "@/constants";
import { useI18n } from "@/contexts/i18nContext";
import { useMentionCards } from "@/contexts/MentionCardsContext";
import { useConditionFlaggedCallback } from "@/hooks/useConditionFlaggedCallback";
import { useUser } from "@/hooks/useUser";
import { ROUTES } from "@/routes_new";
import { EVENT } from "@/tracking_events";
import { SearchFiltersFull } from "@/types/SearchFilter";
import { User } from "@/types/User";
import { formatDateForApi } from "@/utils";
import { poll } from "@/views/reports/widgets/helpers";
import Button from "@components/Button";
import SpinnerAnimated from "@components/SpinnerAnimated";
import TextButton, { TextButtonProps } from "@components/TextButton";
import Typography from "@components/Typography";

import { useAppliedAlertParams } from "./useAppliedAlertParams";
import { useHasFeature } from "@/hooks/useHasFeature";
import { FEATURE } from "@/features";

type PptTask = { task_status: TaskStatus; data: string };
type PptResponse = { data: PptTask };
type Task = undefined | { task_id: string; task_type: string };

export const PowerPoint = ({
  hasPptFeature,
  reportUuid,
  filters,
  ...rest
}: {
  hasPptFeature: boolean;
  reportUuid?: string | null;
  filters: Partial<SearchFiltersFull>;
}) => {
  const [task, setTask] = useState<Task>(undefined);
  const [url, setUrl] = useState();
  const { language, currency } = useI18n();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const startPPTgeneration = () => {
    postTrackingData(EVENT.GENERATE_PPT);
    setLoading(true);

    newTask("generate_powerpoint", {
      start_date:
        filters?.start_date || formatDateForApi(sub(new Date(), { months: 3 })),
      end_date: filters?.end_date || formatDateForApi(new Date()),
      insights_report_uuid: reportUuid,
      filters,
      language,
      currency,
    })
      .then(({ data }) => setTask(data as Task))
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  };

  useEffect(() => {
    let mounted = true;
    if (task) {
      poll({
        fn: () => getTaskStatus(task.task_type, task.task_id),
        validate: ({ data }: PptResponse) =>
          data.task_status === TASK_STATUS.SUCCESS,
        interval: 3000,
        maxAttempts: 150,
        checkError: ({ data }: PptResponse) =>
          data.task_status === TASK_STATUS.FAILURE,
      })
        .then(({ data }) => {
          // set the data if the component is still mounted
          setLoading(false);
          mounted && data && setUrl(data.data);
          // ... otherwise try downloading the file anyway by opening the URL.
          window.open(data.data, "_blank");
        })
        .catch(() => {
          if (!mounted) return;
          setLoading(false);
        });
    }

    return () => {
      mounted = false;
    };
  }, [task]);

  const onClickDownload = url
    ? () => {
        postTrackingData(EVENT.DOWNLOAD_PPT);
        window.open(url, "_blank");
      }
    : loading
      ? undefined
      : startPPTgeneration;

  const onClickPPT = useConditionFlaggedCallback(
    hasPptFeature,
    onClickDownload,
    true
  );

  if (onClickPPT === undefined) return null;

  if (loading) {
    return <SpinnerAnimated width="2rem" />;
  }

  if (error) {
    return (
      <Typography margin="none">
        {L.SEARCH_TOP_WIDGET_TITLE_POWERPOINT_ERROR}
      </Typography>
    );
  }

  return (
    <Button
      target="_blank"
      data-testid="download-ppt"
      variant="secondary"
      disabled={loading}
      onClick={() => {
        onClickPPT?.();
      }}
      {...rest}
    >
      {url
        ? L.SEARCH_TOP_WIDGET_TITLE_POWERPOINT_DOWNLOAD
        : L.MENTIONS_VIEW_TOOLBAR_EXPORT_POWERPOINT}
    </Button>
  );
};

export const Excel = (props: TextButtonProps) => {
  const { alert } = useMentionCards();
  const stringifiedFilters = useAppliedAlertParams();

  const url = `${generatePath(ROUTES.SEARCH_EXPORT_EXCEL, {
    search_uuid: alert?.uuid,
  })}?${stringifiedFilters}`;

  const hasExcelExportFeature = useHasFeature(FEATURE.HAS_EXCEL_EXPORT_FEATURE);

  return (
    <Button
      href={url}
      target="_blank"
      variant="secondary"
      data-testid="excel-export-link"
      disabled={!hasExcelExportFeature}
      {...props}
    >
      {L.MENTIONS_VIEW_TOOLBAR_EXPORT_EXCEL}
    </Button>
  );
};

export const Csv = (props: TextButtonProps) => {
  // csv is not referenced outside mentions feed, so always v4
  const { alert } = useMentionCards();

  const stringifiedFilters = useAppliedAlertParams();

  const url = `${generatePath(ROUTES.SEARCH_EXPORT_CSV, {
    search_uuid: alert?.uuid,
  })}?${stringifiedFilters}`;

  const hasExcelExportFeature = useHasFeature(FEATURE.HAS_EXCEL_EXPORT_FEATURE);

  return (
    <Button
      href={url}
      target="_blank"
      disabled={!hasExcelExportFeature}
      {...props}
    >
      {L.MENTIONS_VIEW_TOOLBAR_EXPORT_CSV}
    </Button>
  );
};

export const Json = (props: TextButtonProps) => {
  const { alert } = useMentionCards();
  const url = generatePath(ROUTES.SEARCH_EXPORT_API, {
    search_uuid: alert?.uuid,
  });

  const hasJsonAPIFeature = useHasFeature(FEATURE.HAS_JSON_API_FEATURE);

  return (
    <TextButton
      color="primary"
      href={url}
      target="_blank"
      icon={ArrowTopRightOnSquareIcon}
      disabled={!hasJsonAPIFeature}
      {...props}
    >
      {L.MENTIONS_VIEW_TOOLBAR_EXPORT_JSON_API}
    </TextButton>
  );
};

export const Rss = (props: TextButtonProps) => {
  const { alert } = useMentionCards();
  const { data } = useUser();
  const language = (data?.data as User)?.language;
  const url = generatePath(ROUTES.SEARCH_EXPORT_RSS, {
    search_uuid: alert?.uuid,
    language,
  });
  return (
    <TextButton
      color="primary"
      href={url}
      target="_blank"
      icon={ArrowTopRightOnSquareIcon}
      {...props}
    >
      {L.MENTIONS_VIEW_TOOLBAR_EXPORT_RSS}
    </TextButton>
  );
};
